// Match Lab review helpers, shared by the label review
// (MatchLabLabelMode.tsx): the possible-goal kick-off rule, the scan
// constants, and the detector scorecard. Review mode's own queue and panel
// were merged into the label review (Ben, 2026-10-22).
import type { CSSProperties } from 'react'

import type { PitchEvent } from './MatchLabPitch'

/** A kick-off restarts play after a goal, so one with no detected goal in
 *  the time before it may follow a goal the detector missed: its clip is
 *  that stretch, played at the scan speed. */
export const POSSIBLE_GOAL_LOOKBACK_S = 90
/** A kick-off this close to its period's start opens the half, not a
 *  restart after a goal. */
export const PERIOD_KICKOFF_S = 30

/** Scan: between moments the video plays at this speed instead of
 *  jumping, and slows to the clip speed at the next clip's start. Gaps
 *  longer than SCAN_MAX_GAP_S are jumped anyway. */
export const SCAN_RATES = [4, 6, 8, 12]
export const SCAN_MAX_GAP_S = 300

type Period = { period_id: number; start_frame_idx: number; end_frame_idx: number }

const isKickOff = (e: PitchEvent) => e.kind === 'set_piece' && e.detail === 'kick_off'

/** Kick-offs that do not open a period and have no (unrejected) goal in
 *  the POSSIBLE_GOAL_LOOKBACK_S before them. Without periods a half's
 *  opening kick-off cannot be told apart, so every kick-off is listed. */
export function possibleGoalKickOffs(events: PitchEvent[], periods: Period[],
                                     fps: number): PitchEvent[] {
  const goals = events.filter((e) => e.kind === 'goal'
                                     && e.review?.action !== 'reject')
  const lookback = POSSIBLE_GOAL_LOOKBACK_S * fps
  const opening = PERIOD_KICKOFF_S * fps
  return events.filter((e) => isKickOff(e)
    && e.review?.action !== 'reject'
    && !periods.some((p) => e.frame_idx >= p.start_frame_idx
                            && e.frame_idx - p.start_frame_idx <= opening)
    && !goals.some((g) => g.frame_idx <= e.frame_idx
                          && e.frame_idx - g.frame_idx <= lookback))
}

// ── scorecard: the detector graded against the reviews and labels ──

export type KindScore = {
  detected: number; tp: number; fp: number; fn: number; unlabelled: number
  precision: number | null; recall: number | null; type_agree: number | null
}
export type EventScores = Record<'goal' | 'shot' | 'set_piece', KindScore>

const pct = (v: number | null) => v == null ? '—' : `${Math.round(v * 100)}%`

export function Scorecard({ scores, error }: { scores: EventScores | null; error: string | null }) {
  if (error) return <div style={{ marginTop: '6px', opacity: 0.7 }}>Scores unavailable: {error}</div>
  if (!scores) return <div style={{ marginTop: '6px', opacity: 0.7 }}>Scoring{'…'}</div>
  const cell: CSSProperties = { padding: '1px 6px', textAlign: 'right' }
  return (
    <table aria-label="Detector scores"
           style={{ marginTop: '6px', borderCollapse: 'collapse', fontSize: '11px' }}>
      <thead>
        <tr style={{ opacity: 0.6 }}>
          <th style={{ textAlign: 'left' }}>Detector</th>
          <th style={cell}>found</th><th style={cell}>right</th>
          <th style={cell}>wrong</th><th style={cell}>missed</th>
          <th style={cell}>precision</th><th style={cell}>recall</th>
          <th style={cell}>type</th>
        </tr>
      </thead>
      <tbody>
        {(['goal', 'shot', 'set_piece'] as const).map((k) => {
          const s = scores[k]
          return (
            <tr key={k}>
              <td>{k === 'set_piece' ? 'Restarts' : k === 'goal' ? 'Goals' : 'Shots'}</td>
              <td style={cell}>{s.detected}</td><td style={cell}>{s.tp}</td>
              <td style={cell}>{s.fp}</td><td style={cell}>{s.fn}</td>
              <td style={cell}>{pct(s.precision)}</td>
              <td style={cell}>{pct(s.recall)}</td>
              <td style={cell}>{pct(s.type_agree)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
